/**
 * Payroll settings — the legal entities payroll runs against, their statutory registrations, the
 * effective-dated statutory rules that apply to them, and their loan / earned-wage-access limits.
 *
 * ONE COMPANY MASTER. A payroll entity IS an `organisation.legalEntity` row; this contract adds the
 * registrations payroll needs on top of it rather than keeping a second list of companies. Creating
 * an entity here creates the legal entity and its registrations in one transaction.
 *
 * COMPLETENESS IS NOT VERIFICATION. `registrationsCompleted` counts identifiers that are recorded,
 * against the ones that apply to that entity. Nothing here claims a registration was checked with
 * the issuing authority — no such integration exists.
 *
 * Money and percentages are decimal strings (invariant 2). Statutory rules are effective-dated
 * (invariant 5): a new version closes the previous one instead of overwriting it.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { CORPORATE_ID_TYPES, STATUTORY_RULE_TYPES } from '../enums.js';
import {
  countrySchema,
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  inStateSchema,
  instantSchema,
  orgIdSchema,
  timezoneSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import { ptVariantConditionShape, refinePtVariantConditions } from '../schemas/professional-tax.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });
const entityParamsSchema = z.object({ legalEntityId: uuidSchema });

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export const corporateIdTypeSchema = z.enum(CORPORATE_ID_TYPES);
export const statutoryRuleTypeSchema = z.enum(STATUTORY_RULE_TYPES);

/** A company PAN: five letters, four digits, one letter. */
export const entityPanSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, 'Must be a 10-character PAN, e.g. AAACV7812M');

/** TAN: four letters, five digits, one letter. */
export const tanSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{4}[0-9]{5}[A-Z]$/, 'Must be a 10-character TAN, e.g. BLRV09121E');

const CIN_PATTERN = /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;
const LLPIN_PATTERN = /^[A-Z]{3}-[0-9]{4}$/;

/** A registration number with no national format to check — trimmed, bounded, uppercased. */
const registrationNumberSchema = z.string().trim().toUpperCase().min(1).max(40);

/**
 * 0–100 with up to four decimal places.
 *
 * Deliberately NOT `schemas/common.ts`'s `percentSchema`, which is capped at TWO places: a
 * statutory rate is published to four (ESI's 0.7500% employee share, PF's 12.0000%), and rounding
 * one to 2dp changes what a person is actually deducted. Named distinctly because both are
 * re-exported from the package barrel, where two `percentSchema` exports are a TS2308 ambiguity.
 */
export const ratePercentSchema = decimalStringSchema.refine((value) => {
  const n = Number(value);
  return n >= 0 && n <= 100;
}, 'Must be between 0 and 100');

/** A monthly/annual amount that cannot be negative. */
export const nonNegativeAmountSchema = decimalStringSchema.refine(
  (value) => !value.startsWith('-'),
  'Must not be negative',
);

// ---------------------------------------------------------------------------
// Entities & registrations
// ---------------------------------------------------------------------------

export const payrollEntitySchema = z.object({
  legalEntityId: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  country: z.string(),
  currency: z.string(),
  fiscalYearStartMonth: z.number().int().min(1).max(12),
  timezone: z.string(),
  stateCode: z.string().nullable(),
  city: z.string().nullable(),
  isActive: z.boolean(),
  corporateIdType: corporateIdTypeSchema,
  corporateId: z.string().nullable(),
  /** Never the full value in a list — e.g. "AAA*****2M". Null when no PAN is on file. */
  panMasked: z.string().nullable(),
  tan: z.string().nullable(),
  pfEstablishmentCode: z.string().nullable(),
  esicCode: z.string().nullable(),
  ptStateCode: z.string().nullable(),
  ptRegistrationNumber: z.string().nullable(),
  lwfStateCode: z.string().nullable(),
  lwfRegistrationNumber: z.string().nullable(),
  autoStatutoryDeductions: z.boolean(),
  /**
   * Pay and TDS in whole rupees: every earning, PF, PT, LWF and the monthly TDS rounded to the nearest rupee (reimbursements and
   * recoveries stay exact). The value in force: an entity that never chose follows its country (on in India). Optional so an
   * older server's payload still parses.
   */
  roundPayToRupee: z.boolean().optional(),
  /** Identifiers recorded, out of those that apply to this entity (see the header). */
  registrationsCompleted: z.number().int().nonnegative(),
  registrationsApplicable: z.number().int().nonnegative(),
  /** 0 when no payroll registrations have been saved yet. Echo it back on update. */
  settingsVersion: z.number().int().nonnegative(),
});
export type PayrollEntity = z.infer<typeof payrollEntitySchema>;

/** The edit form's view — the one place the full PAN is returned, and only to a manager. */
export const payrollEntityDetailSchema = payrollEntitySchema.extend({
  pan: z.string().nullable(),
});
export type PayrollEntityDetail = z.infer<typeof payrollEntityDetailSchema>;

const registrationsFields = {
  corporateIdType: corporateIdTypeSchema,
  corporateId: z.string().trim().toUpperCase().max(30).nullable().optional(),
  pan: entityPanSchema.nullable().optional(),
  tan: tanSchema.nullable().optional(),
  pfEstablishmentCode: registrationNumberSchema.nullable().optional(),
  esicCode: z
    .string()
    .trim()
    .regex(/^[0-9]{10,17}$/, 'ESIC codes are 10–17 digits')
    .nullable()
    .optional(),
  /** The PT jurisdiction — explicit, never silently copied from the entity's address. */
  ptStateCode: inStateSchema.nullable().optional(),
  ptRegistrationNumber: registrationNumberSchema.nullable().optional(),
  lwfStateCode: inStateSchema.nullable().optional(),
  lwfRegistrationNumber: registrationNumberSchema.nullable().optional(),
  /** Recorded as a setting; it does not by itself make payroll deduct anything. */
  autoStatutoryDeductions: z.boolean().optional(),
  /** Pay and TDS in whole rupees (see `payrollEntitySchema.roundPayToRupee`). Omitted: unchanged. */
  roundPayToRupee: z.boolean().optional(),
};

function corporateIdMatchesType(value: {
  corporateIdType?: z.infer<typeof corporateIdTypeSchema>;
  corporateId?: string | null;
}): boolean {
  if (!value.corporateId) return true;
  if (value.corporateIdType === 'cin') return CIN_PATTERN.test(value.corporateId);
  if (value.corporateIdType === 'llpin') return LLPIN_PATTERN.test(value.corporateId);
  return true;
}

const corporateIdMessage = {
  message: 'A CIN is 21 characters (e.g. U72200KA2018PTC112480); an LLPIN looks like AAB-4412',
  path: ['corporateId'],
};

export const payrollRegistrationsInputSchema = z
  .object(registrationsFields)
  .refine(corporateIdMatchesType, corporateIdMessage);

/**
 * Creates the legal entity and its payroll registrations together. The entity fields are the ones
 * `organisation.createLegalEntity` requires — the Stitch design did not show them, but an entity
 * cannot exist without a code, country, currency, fiscal-year start, and timezone.
 */
export const createPayrollEntityBodySchema = z.object({
  entity: z.object({
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9][A-Z0-9-]{1,19}$/, '2–20 characters: letters, digits, hyphens'),
    name: z.string().trim().min(1).max(200),
    country: countrySchema,
    currency: currencySchema,
    fiscalYearStartMonth: z.number().int().min(1).max(12),
    timezone: timezoneSchema,
    stateCode: z.string().trim().max(10).optional(),
    city: z.string().trim().max(100).optional(),
  }),
  registrations: payrollRegistrationsInputSchema,
});

/**
 * `code` and `country` are immutable, matching the organisation entity form. `pan` omitted means
 * "unchanged" — the client only ever holds a masked copy unless it loaded the detail view.
 * `expectedVersion` rejects an edit made against a stale copy (409).
 */
export const updatePayrollEntityBodySchema = z.object({
  expectedVersion: z.number().int().nonnegative(),
  entity: z
    .object({
      name: z.string().trim().min(1).max(200).optional(),
      currency: currencySchema.optional(),
      fiscalYearStartMonth: z.number().int().min(1).max(12).optional(),
      timezone: timezoneSchema.optional(),
      stateCode: z.string().trim().max(10).nullable().optional(),
      city: z.string().trim().max(100).nullable().optional(),
      isActive: z.boolean().optional(),
    })
    .default({}),
  registrations: z
    .object({ ...registrationsFields, corporateIdType: corporateIdTypeSchema.optional() })
    .refine(corporateIdMatchesType, corporateIdMessage),
});

export const payrollSettingsSummarySchema = z.object({
  operatingEntities: z.number().int().nonnegative(),
  registrationsCompleted: z.number().int().nonnegative(),
  registrationsApplicable: z.number().int().nonnegative(),
  entitiesWithGaps: z.number().int().nonnegative(),
  /** Entities that have a current PF, ESI, PT, or LWF rule on file. */
  entitiesWithStatutoryRules: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Statutory rules
// ---------------------------------------------------------------------------

export const ptSlabSchema = z.object({
  fromAmount: nonNegativeAmountSchema,
  /** Null for the open-ended top slab. */
  toAmount: nonNegativeAmountSchema.nullable(),
  monthlyTax: nonNegativeAmountSchema,
});

/**
 * The provident-fund rule a company records (ADR 0021). The wage ceiling, the rates, the pension rate and the EPFO charges are
 * fixed by notification, so they are NOT a company's to enter: the platform holds them by date and the calculation, the return
 * and the settings screen read them from there. What is left to the company is the election the law leaves optional.
 *
 * The three figures below are kept, optional, ONLY so a version stored before this change still parses. They are no longer
 * written by the settings screen and, where the platform holds a value for the period, are not read.
 */
export const pfRuleConfigSchema = z.object({
  /**
   * Contribute on the ceiling rather than the actual Basic + DA when wages exceed it. `false` records the joint written opt-in
   * of employee and employer to contribute on the actual wage (EPF Scheme para 9(4)).
   */
  restrictToCeiling: z.boolean(),
  /** @deprecated stored before ADR 0021; the platform's value for the period wins. */
  employeeRatePercent: ratePercentSchema.optional(),
  /** @deprecated stored before ADR 0021; the platform's value for the period wins. */
  employerRatePercent: ratePercentSchema.optional(),
  /** @deprecated stored before ADR 0021; the platform's value for the period wins. */
  wageCeiling: nonNegativeAmountSchema.optional(),
});

export const esiRuleConfigSchema = z.object({
  employeeRatePercent: ratePercentSchema,
  employerRatePercent: ratePercentSchema,
  /** Gross monthly wages at or below this are covered. */
  wageLimit: nonNegativeAmountSchema,
});

/**
 * A table that applies INSTEAD of the default `slabs` to a stated gender and/or stated months (ADR 0019): a
 * gender-specific threshold, a February top-up, a half-yearly instalment. Tried in order, first match wins; what a
 * state's schedule says is data a tenant records, never something this contract knows.
 */
export const ptVariantSchema = z
  .object({
    ...ptVariantConditionShape,
    slabs: z.array(ptSlabSchema).min(1).max(20),
  })
  .superRefine(refinePtVariantConditions);

export type PtRuleVariant = z.infer<typeof ptVariantSchema>;

export const ptRuleConfigSchema = z.object({
  stateCode: inStateSchema,
  /** The default table: applies when no variant does. */
  slabs: z.array(ptSlabSchema).min(1).max(20),
  /** Optional, so every configuration stored before ADR 0019 stays valid and means exactly what it did. */
  variants: z.array(ptVariantSchema).max(12).optional(),
});

export const lwfRuleConfigSchema = z.object({
  stateCode: inStateSchema,
  employeeContribution: nonNegativeAmountSchema,
  employerContribution: nonNegativeAmountSchema,
  frequency: z.enum(['monthly', 'half_yearly', 'yearly']),
  /** 1–12, the months the contribution is deducted in. */
  deductionMonths: z.array(z.number().int().min(1).max(12)).min(1).max(12),
});

export const gratuityRuleConfigSchema = z.object({
  daysPerYearOfService: z.number().int().min(1).max(60),
  divisorDays: z.number().int().min(1).max(31),
  minYearsOfService: z.number().int().min(0).max(10),
  /** Monthly provision as a percentage of Basic, e.g. "4.81". */
  provisionPercentOfBasic: ratePercentSchema,
});

export const bonusRuleConfigSchema = z.object({
  minPercent: ratePercentSchema,
  maxPercent: ratePercentSchema,
  wageCeiling: nonNegativeAmountSchema,
  eligibilityWageLimit: nonNegativeAmountSchema,
});

export const statutoryRuleInputSchema = z.discriminatedUnion('ruleType', [
  z.object({ ruleType: z.literal('pf'), config: pfRuleConfigSchema }),
  z.object({ ruleType: z.literal('esi'), config: esiRuleConfigSchema }),
  z.object({ ruleType: z.literal('pt'), config: ptRuleConfigSchema }),
  z.object({ ruleType: z.literal('lwf'), config: lwfRuleConfigSchema }),
  z.object({ ruleType: z.literal('gratuity'), config: gratuityRuleConfigSchema }),
  z.object({ ruleType: z.literal('bonus'), config: bonusRuleConfigSchema }),
]);
export type StatutoryRuleInput = z.infer<typeof statutoryRuleInputSchema>;

export const statutoryRuleVersionSchema = z.object({
  id: uuidSchema,
  legalEntityId: uuidSchema,
  rule: statutoryRuleInputSchema,
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
  /** Relative to today in the entity's timezone. */
  state: z.enum(['current', 'scheduled', 'superseded']),
  note: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: instantSchema,
});
export type StatutoryRuleVersion = z.infer<typeof statutoryRuleVersionSchema>;

export const listStatutoryRulesQuerySchema = z.object({
  legalEntityId: uuidSchema,
  ruleType: statutoryRuleTypeSchema.optional(),
  /** Superseded versions are left out unless asked for. */
  includeHistory: z.coerce.boolean().optional(),
});

/**
 * The figures the platform holds for a rule type on a date, shown read-only next to what a company may set (ADR 0021). Only the
 * provident fund has any so far; the other types return `supported: false` and no parameters.
 */
export const platformParameterSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.string(),
  unit: z.enum(['inr', 'percent']),
  /** How far the figure has been checked against a notification. Never hidden from the person reading it. */
  verification: z.enum(['verified', 'partly_verified', 'unverified']),
  /** Where the figure comes from, in words, and a link when there is one. */
  source: z.string(),
  sourceRef: z.string().nullable(),
});
export type PlatformParameter = z.infer<typeof platformParameterSchema>;

export const statutoryPlatformParametersQuerySchema = z.object({
  legalEntityId: uuidSchema,
  ruleType: statutoryRuleTypeSchema,
  /** Defaults to today in the entity's timezone. */
  asOf: dateOnlySchema.optional(),
});

export const statutoryPlatformParametersSchema = z.object({
  ruleType: statutoryRuleTypeSchema,
  asOf: dateOnlySchema,
  /** False for a rule type the platform holds no figures for yet. */
  supported: z.boolean(),
  /** The window the figures are in force for; null when the platform holds none for the date. */
  validFrom: dateOnlySchema.nullable(),
  validTo: dateOnlySchema.nullable(),
  parameters: z.array(platformParameterSchema),
});
export type StatutoryPlatformParameters = z.infer<typeof statutoryPlatformParametersSchema>;

export const createStatutoryRuleVersionBodySchema = z.object({
  legalEntityId: uuidSchema,
  validFrom: dateOnlySchema,
  note: z.string().trim().max(500).optional(),
  rule: statutoryRuleInputSchema,
});

// ---------------------------------------------------------------------------
// Loans & earned wage access
// ---------------------------------------------------------------------------

export const loanEwaSettingsSchema = z.object({
  legalEntityId: uuidSchema,
  currency: z.string(),
  advancesEnabled: z.boolean(),
  maxAdvancePercentOfGross: decimalStringSchema.nullable(),
  advanceCapAmount: decimalStringSchema.nullable(),
  maxLoanTenureMonths: z.number().int().nullable(),
  ewaEnabled: z.boolean(),
  ewaProvider: z.string().nullable(),
  /**
   * `not_connected` for any partner name — no real EWA partner integration exists, so switching one
   * on is recorded intent, never a working integration. `sandbox` only when the provider is
   * literally "sandbox": a built-in simulator that follows the real request/reserve/callback path
   * and moves no money.
   */
  ewaIntegrationStatus: z.enum(['not_connected', 'sandbox']),
  /** Of wages already earned this cycle, the most that may be drawn early. Required to enable EWA. */
  ewaMaxPercentOfAccrued: decimalStringSchema.nullable(),
  /** Flat fee per withdrawal, deducted from the payout. Null or zero means none. */
  ewaFeeAmount: decimalStringSchema.nullable(),
  treasuryPoolAmount: decimalStringSchema.nullable(),
  /** 0 when nothing has been saved yet and the values are defaults. */
  version: z.number().int().nonnegative(),
  updatedAt: instantSchema.nullable(),
});
export type LoanEwaSettings = z.infer<typeof loanEwaSettingsSchema>;

export const updateLoanEwaSettingsBodySchema = z
  .object({
    expectedVersion: z.number().int().nonnegative(),
    advancesEnabled: z.boolean(),
    maxAdvancePercentOfGross: ratePercentSchema.nullable(),
    advanceCapAmount: nonNegativeAmountSchema.nullable(),
    maxLoanTenureMonths: z.number().int().min(1).max(120).nullable(),
    ewaEnabled: z.boolean(),
    ewaProvider: z.string().trim().max(100).nullable(),
    treasuryPoolAmount: nonNegativeAmountSchema.nullable(),
    ewaMaxPercentOfAccrued: ratePercentSchema.nullable().optional(),
    ewaFeeAmount: nonNegativeAmountSchema.nullable().optional(),
  })
  .refine((body) => !body.advancesEnabled || body.maxAdvancePercentOfGross !== null, {
    message: 'Set a maximum advance percentage before enabling advances',
    path: ['maxAdvancePercentOfGross'],
  })
  .refine(
    (body) =>
      !body.ewaEnabled ||
      (body.ewaMaxPercentOfAccrued !== null && body.ewaMaxPercentOfAccrued !== undefined),
    {
      message: 'Set how much of earned wages may be drawn before enabling EWA',
      path: ['ewaMaxPercentOfAccrued'],
    },
  );

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const payrollSettingsContract = c.router(
  {
    getSummary: {
      method: 'GET',
      path: '/summary',
      responses: { 200: payrollSettingsSummarySchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Headline figures for the Company & Statutory screen',
      metadata: routeMeta('payroll:read'),
    },

    listEntities: {
      method: 'GET',
      path: '/entities',
      query: z.object({ includeInactive: z.coerce.boolean().optional() }),
      responses: {
        200: z.object({ items: z.array(payrollEntitySchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List legal entities with their payroll registrations',
      metadata: routeMeta('payroll:read'),
    },

    getEntity: {
      method: 'GET',
      path: '/entities/:legalEntityId',
      pathParams: entityParamsSchema,
      responses: {
        200: payrollEntityDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One entity for editing, including the full PAN',
      metadata: routeMeta('payroll:read'),
    },

    createEntity: {
      method: 'POST',
      path: '/entities',
      headers: idempotencyHeadersSchema,
      body: createPayrollEntityBodySchema,
      responses: {
        201: payrollEntityDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create a legal entity and its payroll registrations together',
      metadata: routeMeta(['legal_entity:create', 'payroll.rules:manage']),
    },

    updateEntity: {
      method: 'PATCH',
      path: '/entities/:legalEntityId',
      pathParams: entityParamsSchema,
      body: updatePayrollEntityBodySchema,
      responses: {
        200: payrollEntityDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `expectedVersion` is stale — someone else saved first. */
        409: errorBodySchema,
      },
      summary: "Update an entity's details and payroll registrations",
      metadata: routeMeta(['legal_entity:update', 'payroll.rules:manage', 'payroll:read']),
    },

    listStatutoryRules: {
      method: 'GET',
      path: '/statutory-rules',
      query: listStatutoryRulesQuerySchema,
      responses: {
        200: z.object({ items: z.array(statutoryRuleVersionSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List an entity's statutory rule versions",
      metadata: routeMeta(['payroll.rules:read', 'payroll:read']),
    },

    getStatutoryPlatformParameters: {
      method: 'GET',
      path: '/statutory-rules/platform-parameters',
      query: statutoryPlatformParametersQuerySchema,
      responses: {
        200: statutoryPlatformParametersSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The figures the platform holds for a statutory rule type on a date',
      metadata: routeMeta(['payroll.rules:read', 'payroll:read']),
    },

    createStatutoryRuleVersion: {
      method: 'POST',
      path: '/statutory-rules',
      headers: idempotencyHeadersSchema,
      body: createStatutoryRuleVersionBodySchema,
      responses: {
        201: statutoryRuleVersionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `validFrom` is not after the version it would supersede. */
        409: errorBodySchema,
      },
      summary: 'Add a statutory rule version, closing the one it supersedes',
      metadata: routeMeta(['payroll.rules:manage', 'payroll.rules:read', 'payroll:read']),
    },

    getLoanEwaSettings: {
      method: 'GET',
      path: '/entities/:legalEntityId/loan-ewa',
      pathParams: entityParamsSchema,
      responses: {
        200: loanEwaSettingsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "An entity's loan and earned-wage-access limits",
      metadata: routeMeta(['payroll.rules:read', 'payroll:read']),
    },

    updateLoanEwaSettings: {
      method: 'PUT',
      path: '/entities/:legalEntityId/loan-ewa',
      pathParams: entityParamsSchema,
      body: updateLoanEwaSettingsBodySchema,
      responses: {
        200: loanEwaSettingsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: "Save an entity's loan and earned-wage-access limits",
      metadata: routeMeta(['payroll.rules:manage', 'payroll.rules:read', 'payroll:read']),
    },
  },
  { pathPrefix: '/payroll/settings' },
);
